import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { Observable } from 'rxjs';

import {
  EmployerFeeZoneAssignmentCodeJsonFile,
  EmployerFeeZoneAssignmentCodeJsonFileItem,
  EmployerFeeZoneAssignmentCodeJsonFileZoneItem,
} from '../models/hrm-lookup.models';
import { ILocalizedStringDto } from '../models/localized-string.model';
import { BaseHrmLookupClient } from './base-hrm-lookup.client';

@Injectable({
  providedIn: 'root',
})
export class EmployerFeeZoneAssignmentCodesClient extends BaseHrmLookupClient<EmployerFeeZoneAssignmentCodesLookupItem> {
  protected override FILE_PATH = 'generated/employer-fee-zone-assignment-codes.json';

  private readonly locale = inject(LOCALE_ID);

  getEmployerFeeZoneAssignmentCodes(): Observable<EmployerFeeZoneAssignmentCodesLookupItem[]> {
    return this.getCachedOrFetch();
  }

  protected override mapFileToModel(
    data: EmployerFeeZoneAssignmentCodeJsonFile,
  ): EmployerFeeZoneAssignmentCodesLookupItem[] {
    const result: EmployerFeeZoneAssignmentCodesLookupItem[] = [];

    for (const [code, item] of Object.entries(data.employerFeeZoneAssignmentCodes)) {
      const mappedItem: EmployerFeeZoneAssignmentCodesLookupItem = {
        code,
        region: item.r,
        name: this.getLocalizedName(item),
        localizedName: {
          english: item.e ?? '',
          norwegian: item.n,
        },
        zoneAssigmentTimeline: this.mapZoneAssigmentTimeline(item.zt),
      };

      if (item.s) {
        mappedItem.split = item.s;
      }

      result.push(mappedItem);
    }

    return result;
  }

  private mapZoneAssigmentTimeline(
    zoneAssigmentTimeline: EmployerFeeZoneAssignmentCodeJsonFileZoneItem[],
  ): EmployerFeeZoneAssignmentZoneItem[] {
    return zoneAssigmentTimeline.map((item) => {
      const result: EmployerFeeZoneAssignmentZoneItem = { zone: item.z };

      if (item.from) {
        result.from = new Date(item.from);
      }
      if (item.to) {
        result.to = new Date(item.to);
      }

      return result;
    });
  }

  protected getLocalizedName(item: EmployerFeeZoneAssignmentCodeJsonFileItem): string {
    if (this.locale === 'nb-NO') {
      return item.n;
    } else {
      return item.e || item.n; // Fallback to Norwegian if English is not set
    }
  }
}

export type EmployerFeeZoneAssignmentCodesLookupItem = {
  code: string;
  region: string;
  name: string;
  localizedName: ILocalizedStringDto;
  zoneAssigmentTimeline: EmployerFeeZoneAssignmentZoneItem[];
  split?: boolean;
};

export type EmployerFeeZoneAssignmentZoneItem = {
  zone: string;
  from?: Date;
  to?: Date;
};
